import { useState, useEffect, useCallback } from 'react'
import {
  getMedia,
  getPresignedUrl,
  uploadToR2,
  deleteMedia as apiDeleteMedia,
} from '@/lib/api'
import type { MediaItem, PresignRequest } from '@/types'

interface UseMediaReturn {
  media: MediaItem[]
  isLoading: boolean
  error: string | null
  hasMore: boolean
  loadMore: () => Promise<void>
  refresh: () => Promise<void>
  uploadFile: (file: File, onProgress?: (progress: number) => void) => Promise<string>
  deleteMedia: (key: string) => Promise<void>
}

export function useMedia(): UseMediaReturn {
  const [media, setMedia] = useState<MediaItem[]>([])
  const [cursor, setCursor] = useState<string | undefined>()
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchMedia = useCallback(async (append = false) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await getMedia(append ? cursor : undefined)

      if (append) {
        setMedia((prev) => [...prev, ...response.media])
      } else {
        setMedia(response.media)
      }

      setCursor(response.cursor)
      setHasMore(response.hasMore)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement')
    } finally {
      setIsLoading(false)
    }
  }, [cursor])

  useEffect(() => {
    fetchMedia()
  }, [])

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return
    await fetchMedia(true)
  }, [fetchMedia, hasMore, isLoading])

  const refresh = useCallback(async () => {
    setCursor(undefined)
    await fetchMedia()
  }, [fetchMedia])

  const uploadFile = useCallback(
    async (file: File, onProgress?: (progress: number) => void): Promise<string> => {
      try {
        // Get presigned URL
        const presignRequest: PresignRequest = {
          filename: file.name,
          contentType: file.type,
          size: file.size,
        }
        const { uploadUrl, publicUrl } = await getPresignedUrl(presignRequest)

        // Upload file
        await uploadToR2(uploadUrl, file, onProgress)

        // Refresh media list
        await refresh()

        return publicUrl
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erreur d\'upload'
        setError(errorMessage)
        throw new Error(errorMessage)
      }
    },
    [refresh]
  )

  const deleteMedia = useCallback(
    async (key: string) => {
      try {
        await apiDeleteMedia(key)
        setMedia((prev) => prev.filter((m) => m.key !== key))
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erreur de suppression'
        setError(errorMessage)
        throw new Error(errorMessage)
      }
    },
    []
  )

  return {
    media,
    isLoading,
    error,
    hasMore,
    loadMore,
    refresh,
    uploadFile,
    deleteMedia,
  }
}
